import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { BooleanService } from 'src/app/core/services/utelize/general/boolean-service';
import { catchError, firstValueFrom, throwError } from 'rxjs';
import { ComponentAccessUserService } from 'src/app/core/services/utelize/system/component-access/component-access-user-service';
import { ConfigSystemParams, DgAlignment, DgColumnDataType, DgColumnWidths, eSimApiPaths, TypeLicence } from 'src/app/core/consts';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { DgActionColumn } from 'src/app/core/interfaces/datagrid/dg-action-column';
import { DgApiPath } from 'src/app/core/interfaces/datagrid/dg-api-paths';
import { DgColumn } from 'src/app/core/interfaces/datagrid/dg-column';
import { DgPopup } from 'src/app/core/interfaces/datagrid/dg-pop-up';
import { DgProperties } from 'src/app/core/interfaces/datagrid/dg-properties';
import { EncryptionService } from 'src/app/core/services/utelize/general/encryption-service';
import { environment } from 'src/environments/environment';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { GridViewDefaults } from 'src/app/core/grid-view/component/grid-view.defaults';
import { GridViewComponent } from 'src/app/core/grid-view/component/grid-view.component';
import { GridViewService } from 'src/app/core/services/utelize/general/grid-view.service';
import { LicencesCompanyVisibleService } from 'src/app/core/services/utelize/system/licence/licences-company-visible-service';
import { SystemUserDTO } from 'src/app/core/models/system/user/system-user.dto';

const apiPath             =environment.eSimApi+eSimApiPaths.LicencesCompanyVisible + "/" + eSimApiPaths.LicencesCompanyVisibleForSingleCompany;
const exportFilenameSuffix="Licences-Company-Visible-Data";
const popUpTitle          ="User";

@Component({
  selector: 'app-licences-company-visible-search-embedded',
  templateUrl: './licences-company-visible-search.component.html',
  styleUrls: ['./licences-company-visible-search.component.scss'],
  providers: [GridViewService]
})

export class LicencesCompanyVisibleSearchEmbeddedComponent implements AfterViewInit, OnChanges, OnInit {
  
  @Input() companyId              : number | undefined;
  @Input() typeLicenceDropDownDesc: string | undefined;
  @Input() typeLicenceDropDownId  : number | undefined;
  
  @Output() gridRowClick   : EventEmitter<number>=new EventEmitter<number>();
  @Output() gridRowDblClick: EventEmitter<number>=new EventEmitter<number>();
  
  @ViewChild('gridView', { static: false }) gridView!: GridViewComponent;
  
  get enableLicenceBundle    () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.LicencesEnableBundle,           false ); }
  get enableLicenceFeature   () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.LicencesEnableFeature,          false ); }
  get enableLicencePoolBundle() { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.LicencesEnablePoolBundle,       false ); }
  get enableLicenceUsage     () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.LicencesEnableUsage,            false ); }
  get showSystemOwnedToUsers () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.LicencesShowSystemOwnedToUsers, false ); }

  get embeddedGridHeight() { return this.configSystemService.getValueAsInteger(ConfigSystemParams.ModalGridHeightEmbedded, 420); }
  
  private gridViewDefaults!: GridViewDefaults;
  
  public curUser        : SystemUserDTO;
  public dgApiPath!     : DgApiPath | undefined;
  public dgActionColumn!: DgActionColumn | undefined;
  public dgColumns!     : Array<DgColumn>;
  public dgPopup!       : DgPopup | undefined;
  public dgProperties!  : DgProperties | undefined;
  public gotDgProperties: boolean=false;
  
  constructor(
    private authenticationService        : AuthenticationService,
    private booleanService               : BooleanService,
    private componentAccessUserService   : ComponentAccessUserService,
    private configSystemService          : ConfigSystemService,
    private encryptionService            : EncryptionService,
    private exceptionsService            : ExceptionsService,
    private licencesCompanyVisibleService: LicencesCompanyVisibleService
  ) {
    this.curUser         =this.authenticationService.currentUserObject();
    this.gridViewDefaults=new GridViewDefaults(this.authenticationService, this.componentAccessUserService, this.configSystemService, this.exceptionsService);
  }

  public async ngOnInit(): Promise<void> {
    try {
      if (this.companyId==undefined) {
        this.companyId=0;
      }

      if (this.typeLicenceDropDownDesc==undefined || this.typeLicenceDropDownDesc.trim()=="") {
        this.typeLicenceDropDownDesc=TypeLicence.Unknown;
      }

      await this.setUpDgApiPaths();
      this.setUpDgActionColumn();
      this.setUpDgColumns(true);
      this.setUpDgPopup();
      this.setUpDgProperties();

      this.gotDgProperties=true;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngAfterViewInit(): void {
    try {
      this.refilter();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    try {
      
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private async setUpDgApiPaths(): Promise<void> {
    try {
      this.dgApiPath            =this.gridViewDefaults.setUpDgApiPaths(apiPath);
      this.dgApiPath!.refreshGet=() => this.setUpDgApiPathGet();

      await this.setUpDgApiPathGet();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private async setUpDgApiPathGet(): Promise<void> {
    try {
      //CRN_20250903 - Intentional companyId 0, bring back 0 results & let refilter() do the work
      //               Resolves showing all user Licences including SystemOwned, which may need to be hidden

      const encResult     =await firstValueFrom(this.encryptionService.encrypt_AesServerSideForWeb("0", true));
      const companyIdEncSS=encResult.result;

      this.dgApiPath!.get=apiPath + `?companyIdEncSS=${companyIdEncSS}`;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgActionColumn() {
    try {
      this.dgActionColumn               =this.gridViewDefaults.setUpDgActionColumn();
      this.dgActionColumn!.allowDeleting=false;
      this.dgActionColumn!.allowUpdating=false;
      this.dgActionColumn!.visible      =false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgPopup() {
    try {
      this.dgPopup=this.gridViewDefaults.setUpDgPopup(popUpTitle);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgProperties() {
    try {
      this.dgProperties               =this.gridViewDefaults.setUpDgProperties("", exportFilenameSuffix + "-" + this.companyId);
      this.dgProperties!.gridKey      ="EmbeddedLicencesCompanyVisibleSearch";
      this.dgProperties!.height       =this.embeddedGridHeight;
      this.dgProperties!.pageSize     =10;
      this.dgProperties!.showAddButton=false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgColumns(initialCreate: boolean) {
    try {
      let dgColumn    : DgColumn | undefined;
      let dataField   : string;
      let caption     : string;
      let lastColIndex: number=0;
      
      //ID
      if (initialCreate) {
        dgColumn             =this.gridViewDefaults.setUpDgColumn("id", "ID", 0, DgColumnDataType.number);
        dgColumn!.formVisible=false;
        dgColumn!.gridVisible=this.configSystemService.getValueAsBoolean(ConfigSystemParams.GridShowId, false);
        dgColumn!.width      =DgColumnWidths.id;
        this.dgColumns       =[dgColumn!];
      }

      //LicenceType
      dataField="licenceType";
      caption  ="Licence Type";
      
      if (this.typeLicenceDropDownId==undefined || this.typeLicenceDropDownId<=0) {
        lastColIndex++;

        if (!this.dgColumns.find(x => x.dataField==dataField)) {
          dgColumn=this.gridViewDefaults.setUpDgColumn(dataField, caption, "", DgColumnDataType.string);
          //this.dgColumns.push(dgColumn!);
          this.dgColumns.splice(lastColIndex, 0, dgColumn!);
        }
      } else {
        this.dgColumns=this.dgColumns.filter(c => c.dataField !== dataField);
      }

      //LicenceName
      dataField="licenceName";
      caption  ="Licence";
      lastColIndex++;
      
      if (!this.dgColumns.find(x => x.dataField==dataField)) {
        dgColumn=this.gridViewDefaults.setUpDgColumn(dataField, caption, "", DgColumnDataType.string);
        //this.dgColumns.push(dgColumn!);
        this.dgColumns.splice(lastColIndex, 0, dgColumn!);
      }

      //NumberOfEntities - Licences
      dataField="numberOfEntities";
      caption  ="#Licences";
      
      if (this.typeLicenceDropDownDesc==TypeLicence.Platform || this.typeLicenceDropDownDesc==TypeLicence.Unknown) {
        lastColIndex++;

        if (!this.dgColumns.find(x => x.dataField==dataField)) {
          dgColumn           =this.gridViewDefaults.setUpDgColumn(dataField, caption, "", DgColumnDataType.number);
          dgColumn!.alignment=DgAlignment.right;
          dgColumn!.width    =DgColumnWidths.licenceQty;

          dgColumn!.cellTemplate         ="numberOfEntitiesTemplate";
          dgColumn!.cellTemplateType     ="conditional"
          dgColumn!.cellTemplateCondition=(data:any) => {  
            let result = data.licenceType == "Platform";
            return result; 
          };

          //this.dgColumns.push(dgColumn!);
          this.dgColumns.splice(lastColIndex, 0, dgColumn!);
        }
      } else {
        this.dgColumns=this.dgColumns.filter(c => c.dataField !== dataField);
      }

      //ZoneName
      dataField="zoneName";
      caption  ="Zone";

      if (this.typeLicenceDropDownDesc==TypeLicence.Bundle     ||
          this.typeLicenceDropDownDesc==TypeLicence.PoolBundle ||
          this.typeLicenceDropDownDesc==TypeLicence.Unknown    ||
          this.typeLicenceDropDownDesc==TypeLicence.Usage) {
        lastColIndex++;

        if (!this.dgColumns.find(x => x.dataField==dataField)) {
          dgColumn=this.gridViewDefaults.setUpDgColumn(dataField, caption, "", DgColumnDataType.string);
          //this.dgColumns.push(dgColumn!);
          this.dgColumns.splice(lastColIndex, 0, dgColumn!);
        }
      } else {
        this.dgColumns=this.dgColumns.filter(c => c.dataField !== dataField);
      }

      //AllowanceMbOrGb
      dataField="allowanceMbOrGb";
      caption  ="Allowance";

      if (this.typeLicenceDropDownDesc==TypeLicence.Bundle     ||
          this.typeLicenceDropDownDesc==TypeLicence.PoolBundle ||
          this.typeLicenceDropDownDesc==TypeLicence.Unknown) {
        lastColIndex++;

        if (!this.dgColumns.find(x => x.dataField==dataField)) {
          dgColumn                    =this.gridViewDefaults.setUpDgColumn(dataField, caption, "", DgColumnDataType.string);
          dgColumn!.alignment         =DgAlignment.right;
          dgColumn!.calculateCellValue=(rowData: any) => {
            if (rowData?.allowanceMbOrGb == undefined || rowData.allowanceMbOrGb == null)
              return "";

            const unit=rowData.dataWeightCode ?? "GB";

            return `${rowData.allowanceMbOrGb} ${unit}`;
          };
          
          dgColumn!.calculateSortValue=(rowData: any) => this.allowanceInMb(rowData?.allowanceMbOrGb, rowData?.dataWeightCode);
          //this.dgColumns.push(dgColumn!);
          this.dgColumns.splice(lastColIndex, 0, dgColumn!);
        }
      } else {
        this.dgColumns=this.dgColumns.filter(c => c.dataField !== dataField);
      }

      //FeatureName
      dataField="featureName";
      caption  ="Feature";
      
      if (this.typeLicenceDropDownDesc==TypeLicence.Feature || this.typeLicenceDropDownDesc==TypeLicence.Unknown) {
        lastColIndex++;

        if (!this.dgColumns.find(x => x.dataField==dataField)) {
          dgColumn=this.gridViewDefaults.setUpDgColumn(dataField, caption, "", DgColumnDataType.string);
          //this.dgColumns.push(dgColumn!);
          this.dgColumns.splice(lastColIndex, 0, dgColumn!);
        }
      } else {
        this.dgColumns=this.dgColumns.filter(c => c.dataField !== dataField);
      }

      //NumberOfEntities - Feature Quantity
      dataField="numberOfEntitiesFeatureQuantity";
      caption  ="Feat.Qty";
      
      if (this.typeLicenceDropDownDesc==TypeLicence.Feature || this.typeLicenceDropDownDesc==TypeLicence.Unknown) {
        lastColIndex++;

        if (!this.dgColumns.find(x => x.caption==caption)) {
          dgColumn           =this.gridViewDefaults.setUpDgColumn(dataField, caption, "", DgColumnDataType.number);
          dgColumn!.alignment=DgAlignment.right;
          dgColumn!.width    =DgColumnWidths.featureqty;

          dgColumn!.cellTemplate         ="numberOfEntitiesQuantityTemplate";
          dgColumn!.cellTemplateType     ="conditional"
          dgColumn!.cellTemplateCondition=(data:any) => {  
            let result = data.licenceType == "Feature";
            return result; 
          };

          //this.dgColumns.push(dgColumn!);
          this.dgColumns.splice(lastColIndex, 0, dgColumn!);
        }
      } else {
        this.dgColumns=this.dgColumns.filter(c => c.dataField !== dataField);
      }

      //PricePerMb
      dataField="pricePerMb";
      caption  ="Price per MB";
      
      if (this.typeLicenceDropDownDesc==TypeLicence.Unknown || this.typeLicenceDropDownDesc==TypeLicence.Usage) {
        lastColIndex++;

        if (!this.dgColumns.find(x => x.dataField==dataField)) {
          dgColumn           =this.gridViewDefaults.setUpDgColumn(dataField, caption, "", DgColumnDataType.number);
          dgColumn!.alignment=DgAlignment.right;
          //this.dgColumns.push(dgColumn!);
          this.dgColumns.splice(lastColIndex, 0, dgColumn!);
        }
      } else {
        this.dgColumns=this.dgColumns.filter(c => c.dataField !== dataField);
      }

      //Price
      dataField="price";
      caption  ="Price £";
      lastColIndex++;
      
      if (!this.dgColumns.find(x => x.dataField==dataField)) {
        dgColumn           =this.gridViewDefaults.setUpDgColumn(dataField, caption, "", DgColumnDataType.number);
        dgColumn!.alignment=DgAlignment.right;
        //this.dgColumns.push(dgColumn!);
        this.dgColumns.splice(lastColIndex, 0, dgColumn!);
      }

      //SystemOwned
      dataField="systemOwned";
      caption  ="System Owned";
      lastColIndex++;

      if (this.showSystemOwnedToUsers || this.curUser.isUtelize) {
        if (!this.dgColumns.find(x => x.dataField==dataField)) {
          dgColumn            =this.gridViewDefaults.setUpDgColumn(dataField, caption, "", DgColumnDataType.boolean);
          dgColumn!.dataSource=this.booleanService.idAndDescriptionLookupDataSource();
          dgColumn!.width     =DgColumnWidths.systemOwned;
          //this.dgColumns.push(dgColumn!);
          this.dgColumns.splice(lastColIndex, 0, dgColumn!);
        }
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public refilter() {
    try {
      this.licencesCompanyVisibleService.getLicencesCompanyVisibleForSingleCompany(this.companyId ?? 0)
      .pipe(
        catchError((error) => {
          return throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
        })
      )
      .subscribe({
        next: data => {
          data.forEach(row => {
            if (row.zoneNames && row.zoneNames.length>0)
              row.zoneName=row.zoneNames.join(", ");
          });

          this.setUpDgColumns(false);
          this.gridView.clientSideProcessingModels=data;
          this.gridView.clientSideProcessingModels=this.gridView.clientSideProcessingModels.filter(
                                                     x => (
                                                           (
                                                            this.enableLicenceBundle
                                                            ? true
                                                            : x.licenceType!=TypeLicence.Bundle
                                                           )
                                                           &&
                                                           (
                                                            this.enableLicenceFeature
                                                            ? true
                                                            : x.licenceType!=TypeLicence.Feature
                                                           )
                                                           &&
                                                           (
                                                            this.enableLicencePoolBundle
                                                            ? true
                                                            : x.licenceType!=TypeLicence.PoolBundle
                                                           )
                                                           &&
                                                           (
                                                            this.enableLicenceUsage
                                                            ? true
                                                            : x.licenceType!=TypeLicence.Usage
                                                           )
                                                           &&
                                                           (
                                                            (this.typeLicenceDropDownId ?? 0)<=0
                                                            ? true
                                                            : x.licenceTypeID==this.typeLicenceDropDownId
                                                           )
                                                           &&
                                                           (
                                                            (!this.curUser.isUtelize && !this.showSystemOwnedToUsers)
                                                            ? x.systemOwned==false
                                                            : true
                                                           )
                                                          )
                                                   );
          
          this.gridView.dataGrid.instance.refresh();
        },
        error: error => {
          this.exceptionsService.handleException(new Error(error));
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private allowanceInMb(allowance: number | null | undefined, unitCode: string | null | undefined): number {
    try {
      const value=Number(allowance ?? 0);
      const unit =(unitCode ?? "GB").trim().toUpperCase();

      switch (unit) {
        case "KB":
          return value/1024;
        case "MB":
          return value;
        case "GB":
          return value*1024;
        case "TB":
          return value*1024*1024;
        default  :
        return value;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 0;
    }
  }

  public _gridRowClick(rowId: number){
    try {
      this.gridRowClick.emit(rowId);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public _gridRowDblClick(rowId: number){
    try {
      this.gridRowDblClick.emit(rowId);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
